import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { accountsService } from '../../services/accountsService';
import { cardsService } from '../../services/cardsService';
import BooleanPill, { BooleanSelectEditor } from '../Shared/BooleanPill';
import ResourceSidebar from '../Shared/ResourceSidebar';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';
import './CardsPage.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const EMPTY_CARD_FORM = {
  card_name: '',
  owner_username: '',
  status: '',
  is_zen_privacy: false,
  expiry: '',
  ccv: '',
};
const USERNAME_COL_WIDTH = 78;
const STATUS_COL_WIDTH = 92;

const ASSIGNMENT_VENDOR_OPTIONS = [
  { value: 'dice', label: 'Dice', supportsSecondary: true },
  { value: 'aspen', label: 'Belly Up Aspen', supportsSecondary: true },
  { value: 'tao', label: 'Tao', supportsSecondary: false },
];

const ASSIGNMENT_EMPTY_STATE = {
  card: null,
  vendor: 'dice',
  slot: 'primary',
  query: '',
  account: null,
  suggestions: [],
  suggestionsOpen: false,
  suggestionsLoading: false,
  saving: false,
  error: '',
  confirmation: null,
};

const CardsPage = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CARD_FORM);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const [assignment, setAssignment] = useState(ASSIGNMENT_EMPTY_STATE);

  const selectedAssignmentVendor = useMemo(
    () => ASSIGNMENT_VENDOR_OPTIONS.find((option) => option.value === assignment.vendor) || ASSIGNMENT_VENDOR_OPTIONS[0],
    [assignment.vendor],
  );

  const loadCards = async (qValue = '') => {
    setLoading(true);
    setError('');
    const result = await cardsService.listCards({
      q: qValue || undefined,
      limit: 5000,
    });

    if (result.success) {
      setCards(result.items || []);
      setTotalCount(result.totalCount || 0);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadCards('');
  }, []);

  useEffect(() => {
    if (
      !assignment.card
      || !assignment.query.trim()
      || (
        assignment.account?.username
        && assignment.query.trim().toLowerCase() === assignment.account.username.toLowerCase()
      )
    ) {
      setAssignment((current) => ({
        ...current,
        suggestions: [],
        suggestionsOpen: false,
        suggestionsLoading: false,
      }));
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setAssignment((current) => ({ ...current, suggestionsLoading: true, error: '' }));
      const result = await accountsService.listVendorAccounts(assignment.vendor, {
        includeClosed: true,
        includeIssue: true,
        q: assignment.query.trim(),
      });
      if (cancelled) return;
      setAssignment((current) => ({
        ...current,
        suggestionsLoading: false,
        suggestions: result.success ? result.items.slice(0, 8) : [],
        suggestionsOpen: result.success && !!result.items.length,
        error: result.success ? current.error : result.error,
      }));
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [assignment.account?.username, assignment.card, assignment.query, assignment.vendor]);

  const openAssignmentModal = (card) => {
    setAssignment({
      ...ASSIGNMENT_EMPTY_STATE,
      card,
    });
  };

  const closeAssignmentModal = () => {
    if (assignment.saving) return;
    setAssignment(ASSIGNMENT_EMPTY_STATE);
  };

  const updateAssignment = (field, value) => {
    setAssignment((current) => {
      const next = {
        ...current,
        [field]: value,
        error: '',
        confirmation: null,
      };
      if (field === 'vendor') {
        const vendor = ASSIGNMENT_VENDOR_OPTIONS.find((option) => option.value === value) || ASSIGNMENT_VENDOR_OPTIONS[0];
        next.slot = vendor.supportsSecondary ? current.slot : 'primary';
        next.query = '';
        next.account = null;
        next.suggestions = [];
        next.suggestionsOpen = false;
      }
      if (field === 'query') {
        next.account = null;
      }
      return next;
    });
  };

  const selectAssignmentAccount = (account) => {
    setAssignment((current) => ({
      ...current,
      account,
      query: account.username || '',
      suggestions: [],
      suggestionsOpen: false,
      error: '',
      confirmation: null,
    }));
  };

  const currentAssignmentCardNames = (account) => {
    const names = [];
    if (account?.primary_card?.card_name) names.push(`Primary: ${account.primary_card.card_name}`);
    if (account?.secondary_card?.card_name) names.push(`Secondary: ${account.secondary_card.card_name}`);
    return names.join(' | ') || 'No cards assigned';
  };

  const buildAssignmentPayload = () => {
    const cardId = assignment.card?.card_id;
    const account = assignment.account;
    const supportsSecondary = selectedAssignmentVendor.supportsSecondary;
    let primaryCardId = account?.primary_card?.card_id || null;
    let secondaryCardId = supportsSecondary ? (account?.secondary_card?.card_id || null) : null;

    if (assignment.slot === 'secondary') {
      if (!supportsSecondary) {
        throw new Error(`${selectedAssignmentVendor.label} does not support a secondary card.`);
      }
      if (primaryCardId === cardId) {
        primaryCardId = secondaryCardId;
      }
      secondaryCardId = cardId;
    } else {
      if (secondaryCardId === cardId) {
        secondaryCardId = primaryCardId;
      }
      primaryCardId = cardId;
    }

    if (primaryCardId === secondaryCardId) {
      secondaryCardId = null;
    }

    return {
      primary_card_id: primaryCardId,
      secondary_card_id: supportsSecondary ? secondaryCardId : null,
    };
  };

  const saveAssignment = async (confirmSharedCards = false) => {
    if (!assignment.card || !assignment.account) {
      setAssignment((current) => ({ ...current, error: 'Select a vendor account first.' }));
      return;
    }

    let payload;
    try {
      payload = buildAssignmentPayload();
    } catch (err) {
      setAssignment((current) => ({ ...current, error: err.message }));
      return;
    }

    setAssignment((current) => ({ ...current, saving: true, error: '' }));
    const result = await accountsService.manageVendorCards(
      assignment.vendor,
      assignment.account.user_id,
      {
        ...payload,
        confirm_shared_cards: confirmSharedCards,
      },
    );

    if (!result.success) {
      setAssignment((current) => ({ ...current, saving: false, error: result.error }));
      return;
    }

    if (result.confirmationRequired) {
      setAssignment((current) => ({
        ...current,
        saving: false,
        confirmation: result.conflicts || [],
      }));
      return;
    }

    setAssignment(ASSIGNMENT_EMPTY_STATE);
    setMessage(result.message || 'Card assigned.');
    setError('');
    await loadCards(query.trim());
  };

  const columnDefs = useMemo(() => [
    {
      headerName: 'Card Name',
      field: 'card_name',
      minWidth: 260,
      flex: 1.5,
      editable: false,
      pinned: 'left',
    },
    {
      headerName: 'Owner Username',
      field: 'cards_owner_username',
      minWidth: USERNAME_COL_WIDTH,
      width: USERNAME_COL_WIDTH,
      maxWidth: USERNAME_COL_WIDTH,
      editable: false,
    },
    {
      headerName: 'Dice',
      field: 'dice',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Aspen',
      field: 'aspen',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Ticketmaster',
      field: 'ticketmaster',
      minWidth: 140,
      editable: false,
    },
    {
      headerName: 'Get In',
      field: 'get_in',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Seetickets',
      field: 'seetickets',
      minWidth: 130,
      editable: false,
    },
    {
      headerName: 'Axs',
      field: 'axs',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Tao',
      field: 'tao',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Tixr',
      field: 'tixr',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Assign',
      field: 'assign',
      minWidth: 105,
      width: 105,
      maxWidth: 105,
      sortable: false,
      filter: false,
      editable: false,
      cellRenderer: (params) => (
        <button
          className="cards-grid-action"
          type="button"
          onClick={() => openAssignmentModal(params.data)}
        >
          Assign
        </button>
      ),
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: STATUS_COL_WIDTH,
      width: STATUS_COL_WIDTH,
      maxWidth: STATUS_COL_WIDTH,
      editable: true,
      cellClassRules: {
        'status-cell-active': (params) => String(params.value || '').toLowerCase() === 'active',
        'status-cell-closed': (params) => String(params.value || '').toLowerCase() === 'closed',
      },
    },
    {
      headerName: 'Zen/Privacy',
      field: 'is_zen_privacy',
      minWidth: 130,
      editable: true,
      cellEditor: BooleanSelectEditor,
      cellClass: 'cards-boolean-cell',
      cellRenderer: (params) => <BooleanPill value={params.value} />,
      valueParser: (params) => (
        params.newValue === true
        || String(params.newValue || '').trim().toLowerCase() === 'yes'
        || String(params.newValue || '').trim().toLowerCase() === 'true'
      ),
    },
    {
      headerName: 'Expiry',
      field: 'expiry',
      minWidth: 110,
      editable: true,
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
  }), []);

  const handleFilter = (event) => {
    event.preventDefault();
    loadCards(query.trim());
  };

  const openCreateModal = () => {
    setCreateForm(EMPTY_CARD_FORM);
    setCreateError('');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (createSaving) return;
    setShowCreateModal(false);
    setCreateError('');
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const normalizeOptionalText = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed || null;
  };

  const handleCreateCard = async (event) => {
    event.preventDefault();
    const cardName = createForm.card_name.trim();
    const ownerUsername = createForm.owner_username.trim();

    if (!cardName || !ownerUsername) {
      setCreateError('Card Name and Owner are required.');
      return;
    }

    setCreateSaving(true);
    setCreateError('');
    const result = await cardsService.createCard({
      card_name: cardName,
      owner_username: ownerUsername,
      status: normalizeOptionalText(createForm.status),
      is_zen_privacy: createForm.is_zen_privacy,
      expiry: normalizeOptionalText(createForm.expiry),
      ccv: normalizeOptionalText(createForm.ccv),
    });
    setCreateSaving(false);

    if (!result.success) {
      setCreateError(result.error);
      return;
    }

    setCards((current) => [result.item, ...current]);
    setTotalCount((current) => current + 1);
    setMessage(result.message || 'Card created.');
    setError('');
    setShowCreateModal(false);
  };

  const handleCellValueChanged = async (params) => {
    const { data, colDef, oldValue, newValue, node } = params;
    const field = colDef.field;

    if (!field || oldValue === newValue || field === 'card_name' || field === 'owner_username') {
      return;
    }

    const result = await cardsService.updateCard(data.card_id, {
      [field]: newValue === '' ? null : newValue,
    });

    if (!result.success) {
      node.setDataValue(field, oldValue);
      setError(result.error);
      return;
    }

    setError('');
    setMessage(result.message || 'Card updated.');
    setCards((current) => current.map((item) => (
      item.card_id === result.item.card_id ? result.item : item
    )));
    node.setData(result.item);
  };

  return (
    <div className="venues-page cards-page resource-page-with-sidebar">
      <ResourceSidebar />
      <header className="venues-header">
        <div className="venues-header-content">
          <HeaderNewTabButton />
          <h1>Cards</h1>
          <div className="venues-header-actions">
            <button className="btn btn-primary" type="button" onClick={openCreateModal}>
              Add Card
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/user')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="venues-main">
        <form className="venues-filter-form" onSubmit={handleFilter}>
          <div className="form-group">
            <label htmlFor="cards-query">Search</label>
            <input
              id="cards-query"
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="card, owner, vendor assignment, status..."
            />
          </div>
          <button className="btn btn-primary" type="submit">
            Apply
          </button>
        </form>

        <div className="venues-summary">
          <strong>Showing:</strong> {cards.length} cards
          {!!totalCount && <span className="venues-summary-tag">Total: {totalCount}</span>}
        </div>

        {error && <div className="notification error">{error}</div>}
        {message && <div className="notification success">{message}</div>}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading cards...</p>
          </div>
        ) : (
          <div className="venues-grid-shell">
            <div className="ag-theme-quartz-dark venues-ag-grid">
              <AgGridReact
                theme="legacy"
                rowData={cards}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows={true}
                alwaysShowHorizontalScroll={true}
                pagination={true}
                paginationPageSize={100}
                getRowId={(params) => params.data.card_id}
                onCellValueChanged={handleCellValueChanged}
              />
            </div>
          </div>
        )}

        {showCreateModal && (
          <div className="venues-modal-backdrop" onMouseDown={closeCreateModal}>
            <section className="venues-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <h2>Add Card</h2>
                <button className="venues-modal-close" type="button" onClick={closeCreateModal} disabled={createSaving}>
                  x
                </button>
              </div>

              <form className="venues-modal-form" onSubmit={handleCreateCard}>
                <div className="form-group">
                  <label htmlFor="card-name">Card Name</label>
                  <input
                    id="card-name"
                    type="text"
                    value={createForm.card_name}
                    onChange={(event) => handleCreateFormChange('card_name', event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                <div className="form-group cards-owner-field">
                  <label htmlFor="card-owner">Owner</label>
                  <input
                    id="card-owner"
                    type="text"
                    value={createForm.owner_username}
                    onChange={(event) => handleCreateFormChange('owner_username', event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="card-status">Status</label>
                  <input
                    id="card-status"
                    type="text"
                    value={createForm.status}
                    onChange={(event) => handleCreateFormChange('status', event.target.value)}
                    disabled={createSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="card-expiry">Expiry</label>
                  <input
                    id="card-expiry"
                    type="text"
                    value={createForm.expiry}
                    onChange={(event) => handleCreateFormChange('expiry', event.target.value)}
                    disabled={createSaving}
                    placeholder="MM/YY"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="card-ccv">CCV</label>
                  <input
                    id="card-ccv"
                    type="text"
                    value={createForm.ccv}
                    onChange={(event) => handleCreateFormChange('ccv', event.target.value)}
                    disabled={createSaving}
                  />
                </div>

                <label className="cards-checkbox">
                  <input
                    type="checkbox"
                    checked={createForm.is_zen_privacy}
                    onChange={(event) => handleCreateFormChange('is_zen_privacy', event.target.checked)}
                    disabled={createSaving}
                  />
                  <span>Zen/Privacy</span>
                </label>

                {createError && <div className="notification error">{createError}</div>}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCreateModal} disabled={createSaving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={createSaving}>
                    {createSaving ? 'Saving...' : 'Save Card'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

        {assignment.card && (
          <div className="venues-modal-backdrop" onMouseDown={closeAssignmentModal}>
            <section className="venues-modal cards-assignment-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <h2>Assign Card</h2>
                <button className="venues-modal-close" type="button" onClick={closeAssignmentModal} disabled={assignment.saving}>
                  x
                </button>
              </div>

              <div className="venues-modal-form">
                <div className="cards-assignment-card">
                  <span>Card</span>
                  <strong>{assignment.card.card_name}</strong>
                </div>

                <div className="form-group">
                  <label htmlFor="cards-assignment-vendor">Vendor</label>
                  <select
                    id="cards-assignment-vendor"
                    value={assignment.vendor}
                    onChange={(event) => updateAssignment('vendor', event.target.value)}
                    disabled={assignment.saving}
                  >
                    {ASSIGNMENT_VENDOR_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="cards-assignment-slot">Slot</label>
                  <select
                    id="cards-assignment-slot"
                    value={assignment.slot}
                    onChange={(event) => updateAssignment('slot', event.target.value)}
                    disabled={assignment.saving || !selectedAssignmentVendor.supportsSecondary}
                  >
                    <option value="primary">Primary</option>
                    {selectedAssignmentVendor.supportsSecondary ? <option value="secondary">Secondary</option> : null}
                  </select>
                </div>

                <div className="form-group cards-owner-field">
                  <label htmlFor="cards-assignment-account">Vendor Account</label>
                  <input
                    id="cards-assignment-account"
                    type="text"
                    value={assignment.query}
                    onChange={(event) => updateAssignment('query', event.target.value)}
                    placeholder="Search username, email, login, or card..."
                    disabled={assignment.saving}
                    autoComplete="off"
                  />
                  {assignment.suggestionsLoading ? <div className="cards-owner-hint">Searching...</div> : null}
                  {assignment.suggestionsOpen && !!assignment.suggestions.length ? (
                    <div className="cards-owner-suggestions">
                      {assignment.suggestions.map((account) => (
                        <button key={account.user_id} type="button" onClick={() => selectAssignmentAccount(account)}>
                          <strong>{account.username}</strong>
                          <span>{[account.name, account.email, account.login].filter(Boolean).join(' | ')}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {assignment.account ? (
                  <div className="cards-assignment-current">
                    <span>Current account cards</span>
                    <strong>{currentAssignmentCardNames(assignment.account)}</strong>
                  </div>
                ) : null}

                {assignment.confirmation ? (
                  <div className="notification warning">
                    This card is already shared with{' '}
                    {assignment.confirmation.flatMap((conflict) => conflict.usernames || []).join(', ')}.
                    Confirm to keep it shared.
                  </div>
                ) : null}
                {assignment.error && <div className="notification error">{assignment.error}</div>}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeAssignmentModal} disabled={assignment.saving}>
                    Cancel
                  </button>
                  {assignment.confirmation ? (
                    <button className="btn btn-primary" type="button" onClick={() => saveAssignment(true)} disabled={assignment.saving}>
                      {assignment.saving ? 'Saving...' : 'Confirm Shared Card'}
                    </button>
                  ) : (
                    <button className="btn btn-primary" type="button" onClick={() => saveAssignment(false)} disabled={assignment.saving || !assignment.account}>
                      {assignment.saving ? 'Saving...' : 'Assign Card'}
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default CardsPage;
